/**
 * useUserActions Hook
 *
 * Provides admin actions for managing users via Cloud Functions
 * and direct Firestore writes.
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { sendPasswordResetEmail } from 'firebase/auth';
import { functions, auth } from '@/services/firebase';

export type UserRole = 'guest' | 'host' | 'admin';
export type SuspensionDuration = 'temporary' | 'permanent';

interface ChangeRoleParams {
  userId: string;
  role: UserRole;
}

interface SuspendUserParams {
  userId: string;
  reason: string;
  duration: SuspensionDuration;
}

export const useUserActions = () => {
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  const [isUnsuspending, setIsUnsuspending] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const changeRole = useCallback(
    async ({ userId, role }: ChangeRoleParams): Promise<boolean> => {
      setIsChangingRole(true);
      try {
        const fn = httpsCallable(functions, 'updateUserRole');
        await fn({ userId, role });
        return true;
      } catch (error) {
        console.error('Failed to change role:', error);
        return false;
      } finally {
        setIsChangingRole(false);
      }
    },
    []
  );

  const suspendUser = useCallback(
    async ({ userId, reason, duration }: SuspendUserParams): Promise<boolean> => {
      setIsSuspending(true);
      try {
        const fn = httpsCallable(functions, 'suspendUser');
        await fn({ userId, action: 'suspend', reason, duration });
        return true;
      } catch (error) {
        console.error('Failed to suspend user:', error);
        return false;
      } finally {
        setIsSuspending(false);
      }
    },
    []
  );

  const unsuspendUser = useCallback(async (userId: string): Promise<boolean> => {
    setIsUnsuspending(true);
    try {
      const fn = httpsCallable(functions, 'suspendUser');
      await fn({ userId, action: 'unsuspend' });
      return true;
    } catch (error) {
      console.error('Failed to unsuspend user:', error);
      return false;
    } finally {
      setIsUnsuspending(false);
    }
  }, []);

  const [isResendingActivation, setIsResendingActivation] = useState(false);

  // Trigger the branded activation email (via sendAuthEmail) to a host so they
  // can claim their account. The callable enforces a per-email cooldown and
  // creates the Firebase Auth account if needed. Throws on failure so the
  // caller can surface the cooldown message.
  const resendActivationEmail = useCallback(
    async (email: string): Promise<void> => {
      setIsResendingActivation(true);
      try {
        const fn = httpsCallable<{ email: string; type: string }, unknown>(
          functions,
          'sendAuthEmail'
        );
        await fn({ email: email.trim().toLowerCase(), type: 'activation' });
      } finally {
        setIsResendingActivation(false);
      }
    },
    []
  );

  const sendEmail = useCallback(
    async (userId: string, subject: string, _message: string): Promise<boolean> => {
      setIsSendingEmail(true);
      try {
        // Email sending would use a cloud function in production
        console.log(`Send email to user ${userId}: ${subject}`);
        return true;
      } catch (error) {
        console.error('Failed to send email:', error);
        return false;
      } finally {
        setIsSendingEmail(false);
      }
    },
    []
  );

  const sendPasswordResetLink = useCallback(
    async (email: string): Promise<boolean> => {
      setIsResettingPassword(true);
      try {
        await sendPasswordResetEmail(auth, email);
        return true;
      } catch (error) {
        console.error('Failed to send password reset:', error);
        return false;
      } finally {
        setIsResettingPassword(false);
      }
    },
    []
  );

  const bulkChangeRole = useCallback(
    async (userIds: string[], role: UserRole): Promise<boolean> => {
      setIsChangingRole(true);
      try {
        const fn = httpsCallable(functions, 'updateUserRole');
        await Promise.all(userIds.map((userId) => fn({ userId, role })));
        return true;
      } catch (error) {
        console.error('Failed to bulk change role:', error);
        return false;
      } finally {
        setIsChangingRole(false);
      }
    },
    []
  );

  const bulkSuspend = useCallback(
    async (userIds: string[], reason: string): Promise<boolean> => {
      setIsSuspending(true);
      try {
        const fn = httpsCallable(functions, 'suspendUser');
        await Promise.all(
          userIds.map((userId) => fn({ userId, action: 'suspend', reason }))
        );
        return true;
      } catch (error) {
        console.error('Failed to bulk suspend:', error);
        return false;
      } finally {
        setIsSuspending(false);
      }
    },
    []
  );

  return {
    changeRole,
    suspendUser,
    unsuspendUser,
    sendEmail,
    sendPasswordResetLink,
    resendActivationEmail,
    bulkChangeRole,
    bulkSuspend,
    isChangingRole,
    isSuspending,
    isUnsuspending,
    isSendingEmail,
    isResettingPassword,
    isResendingActivation,
  };
};

export default useUserActions;
